import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import dayjs from 'dayjs';
import { ROOT_URL } from '../../api/ApiConfig';
import {
  applyAnalyticsPendingLoading,
  markAnalyticsFetchKey,
} from '../../utils/analyticsQuery';

/* ----------------------------------------------------------
   ✅ Helper function to detect range type from dates
----------------------------------------------------------- */

const detectRangeType = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const now = dayjs();

  if (!start.isValid() || !end.isValid()) return 'custom';

  const diffDays = end.diff(start, 'day');

  // Single day selections
  if (diffDays === 0) {
    if (end.isSame(now, 'day')) return 'today';
    if (end.isSame(now.subtract(1, 'day'), 'day')) return 'yesterday';
    return 'custom';
  }

  if (!end.isSame(now, 'day')) return 'custom';

  if (diffDays === 6 && start.isSame(now.subtract(6, 'day'), 'day')) return '7d';
  if (diffDays === 29 && start.isSame(now.subtract(29, 'day'), 'day')) return '30d';
  if (diffDays === 89 && start.isSame(now.subtract(89, 'day'), 'day')) return '90d';
  if (diffDays === 364 && start.isSame(now.subtract(364, 'day'), 'day')) return '1y';

  return 'custom';
};

/* ----------------------------------------------------------
   ✅ Thunk: Fetch Kb Top Search Terms Stats
----------------------------------------------------------- */
export const getKbTopSearchTermsStats = createAsyncThunk(
  'KbTopSearchTermsStats/getKbTopSearchTermsStats',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authToken = state.loginToken?.response?.token;
      const businessId = state.loginToken?.response?.user?.business_id;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      if (!businessId) throw new Error('Business ID not found');

      let startDate = params.startDate;
      let endDate = params.endDate;
      let range = params.range;
      const group_id = params.group_id;

      if (!startDate || !endDate) {
        endDate = dayjs().format('YYYY-MM-DD');
        startDate = dayjs().subtract(6, 'days').format('YYYY-MM-DD');
        range = '7d';
      }

      if (!range) range = detectRangeType(startDate, endDate);

      let query = `timezone=${timezone}&range=${range}`;
      if (range === 'custom') {
        query += `&from=${startDate}&to=${endDate}`;
      }
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const url = `${ROOT_URL}/analytics/kb/most-searched/${businessId}?${query}`;

      console.log('📊 Requesting Top Search Terms Stats From:', url);

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error fetching Kb top search terms stats:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch Kb top search terms stats'
      );
    }
  }
);

/* ----------------------------------------------------------
   ✅ Thunk: Initialize Default Stats (7-Day Range)
----------------------------------------------------------- */
export const initKbTopSearchTermsStats = createAsyncThunk(
  'KbTopSearchTermsStats/initKbTopSearchTermsStats',
  async ({ group_id } = {}, { dispatch }) => {
    const startDate = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');
    await dispatch(getKbTopSearchTermsStats({ startDate, endDate, range: '7d', group_id }));
  }
);

/* ----------------------------------------------------------
   ✅ Initial State
----------------------------------------------------------- */
const initialState = {
  loading: false,
  error: null,
  data: null,
  lastFetchKey: null,
  keywords: [],   // ← { query: string, count: number }[]
  range: '7d',
  dateRange: {
    startDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  },
};

/* ----------------------------------------------------------
   ✅ Slice Definition
----------------------------------------------------------- */
const KbTopSearchTermsStatsSlice = createSlice({
  name: 'KbTopSearchTermsStats',
  initialState,
  reducers: {
    resetKbTopSearchTermsStats: () => initialState,
    setDateRange: (state, action) => {
      const { startDate, endDate } = action.payload;
      state.dateRange = { startDate, endDate };
      state.range = detectRangeType(startDate, endDate);
    },
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // ── getKbTopSearchTermsStats ──────────────────────────────────────────
      .addCase(getKbTopSearchTermsStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, action.meta?.arg);
        state.error = null;
      })
      .addCase(getKbTopSearchTermsStats.fulfilled, (state, action) => {
        state.loading = false;
        markAnalyticsFetchKey(state, action.meta?.arg);
        state.error = null;

        // API shape:
        // { status, message, data: { range, from, to, keywords: [{query, count}] } }
        const payload = action.payload;
        const inner = payload?.data;

        if (inner) {
          state.data = inner;
          const validKeywords = Array.isArray(inner.keywords)
            ? inner.keywords.filter((k) => k.query !== null && k.query !== undefined && k.query !== '')
            : [];
          state.keywords = validKeywords.length > 0 ? validKeywords : [];
          state.range = inner.range || state.range;
          state.dateRange = {
            startDate: inner.from
              ? dayjs(inner.from).format('YYYY-MM-DD')
              : state.dateRange.startDate,
            endDate: inner.to
              ? dayjs(inner.to).format('YYYY-MM-DD')
              : state.dateRange.endDate,
          };
        } else {
          state.data = payload;
          state.keywords = [];
        }

        console.log('✅ Top search keywords loaded:', state.keywords.length, 'keywords');
      })
      .addCase(getKbTopSearchTermsStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = null;
        state.keywords = [];
        console.error('❌ Top search keywords loading failed:', action.payload);
      })

      // ── initKbTopSearchTermsStats ─────────────────────────────────────────
      .addCase(initKbTopSearchTermsStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, { range: '7d', group_id: action.meta?.arg?.group_id });
      })
      .addCase(initKbTopSearchTermsStats.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initKbTopSearchTermsStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetKbTopSearchTermsStats, setDateRange, clearError } =
  KbTopSearchTermsStatsSlice.actions;

export default KbTopSearchTermsStatsSlice.reducer;